/**
 * Responsibility: hold the player's preferences and persist them.
 *
 * Kept apart from the save for a reason: a world and a set of preferences fail differently. Losing a
 * world means losing where you were; losing preferences means a slider went back to its default. They
 * get separate storage keys so one cannot corrupt the other, and unreadable settings degrade silently
 * to defaults rather than being worth mentioning.
 *
 * Defaults come from `config.js`, so the authored value and the runtime default cannot drift apart.
 *
 * Contains no Three.js and no DOM access beyond the injected storage, so it is fully unit testable.
 */

import { SAVE, BLOOM, CAMERA, INPUT, DEBUG } from '../config.js';

/**
 * Every preference, with its default and how to validate it.
 *
 * A table rather than a series of checks, so adding a preference is one entry and the read path stays
 * generic. `coerce` returns the default for anything it cannot use, which is what makes a hand-edited
 * or half-written blob harmless.
 */
const PREFERENCES = {
  /** Glow strength. The most valuable one to expose: bloom is the most expensive pass. */
  bloomStrength: {
    default: BLOOM.STRENGTH,
    coerce: (value) =>
      Number.isFinite(value)
        ? clamp(value, BLOOM.MIN_STRENGTH, BLOOM.MAX_STRENGTH)
        : BLOOM.STRENGTH,
  },

  /** Up pitches the nose down, for anyone who wants the flight-sim convention. */
  invertPitch: {
    default: INPUT.INVERT_PITCH,
    coerce: (value) => (typeof value === 'boolean' ? value : INPUT.INVERT_PITCH),
  },

  /** Whether the developer readout starts visible. */
  showOverlay: {
    default: DEBUG.SHOW_OVERLAY,
    coerce: (value) => (typeof value === 'boolean' ? value : DEBUG.SHOW_OVERLAY),
  },

  /**
   * How far the camera aims below level, in degrees.
   *
   * Persisted because it is a framing preference found by experiment — in permanent night a level camera spends the top
   * half of the frame on empty black, and how much to trade for terrain is a matter of taste.
   */
  cameraPitchDown: {
    default: CAMERA.PITCH_DOWN,
    coerce: (value) =>
      Number.isFinite(value)
        ? clamp(value, CAMERA.MIN_PITCH_DOWN, CAMERA.MAX_PITCH_DOWN)
        : CAMERA.PITCH_DOWN,
  },
};

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/** The default set, fresh each call so callers cannot mutate a shared object. */
export function defaultSettings() {
  const settings = {};
  for (const [name, spec] of Object.entries(PREFERENCES)) {
    settings[name] = spec.default;
  }
  return settings;
}

/**
 * Normalise a parsed blob into a complete, valid settings object.
 *
 * Field by field rather than all or nothing: one unusable preference should not discard the others.
 * A blob from an older version simply has fewer recognised fields, and the rest fall back — which is
 * why settings need no version migration of their own.
 *
 * @param {unknown} parsed
 * @returns {object}
 */
export function normalizeSettings(parsed) {
  const settings = defaultSettings();
  if (!parsed || typeof parsed !== 'object') return settings;

  for (const [name, spec] of Object.entries(PREFERENCES)) {
    if (name in parsed) {
      settings[name] = spec.coerce(parsed[name]);
    }
  }

  return settings;
}

/**
 * Create the settings store.
 *
 * @param {object} [options]
 * @param {Storage} [options.storage] - Defaults to `localStorage`.
 * @param {string} [options.key]
 * @returns {object}
 */
export function createSettingsStore({ storage = defaultStorage(), key = SAVE.SETTINGS_KEY } = {}) {
  let current = defaultSettings();

  /** Load once at construction. Nothing else re-reads, so an external change needs a reload. */
  if (storage) {
    try {
      const text = storage.getItem(key);
      if (text) {
        const parsed = JSON.parse(text);
        // Version mismatch is not fatal here: unknown fields fall back one at a time.
        current = normalizeSettings(parsed?.settings ?? parsed);
      }
    } catch {
      // Unreadable or malformed settings are not worth reporting. Defaults are a fine outcome.
      current = defaultSettings();
    }
  }

  function persist() {
    if (!storage) return { ok: false };

    try {
      storage.setItem(key, JSON.stringify({ version: SAVE.SETTINGS_VERSION, settings: current }));
      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    }
  }

  return {
    /** A copy, so callers cannot mutate the stored set by accident. */
    getAll() {
      return { ...current };
    },

    /**
     * One preference.
     *
     * @param {string} name
     * @returns {*} The default for an unknown name, rather than undefined — a typo in a caller should
     *   produce sane behaviour rather than a mystery.
     */
    get(name) {
      return name in current ? current[name] : PREFERENCES[name]?.default;
    },

    /**
     * Change one preference and persist immediately.
     *
     * Written on every change rather than batched. Preferences change rarely and by hand, so there is
     * nothing to batch, and a player who closes the tab straight after moving a slider should not lose
     * the change.
     *
     * @param {string} name
     * @param {*} value - Coerced; an unusable value falls back to the default.
     * @returns {*} The value actually stored.
     */
    set(name, value) {
      const spec = PREFERENCES[name];
      if (!spec) {
        throw new Error(`settings: unknown preference "${name}"`);
      }

      current[name] = spec.coerce(value);
      persist();
      return current[name];
    },

    /** Restore every default and persist. */
    reset() {
      current = defaultSettings();
      persist();
      return this.getAll();
    },
  };
}

/** `localStorage`, or null where touching it throws. Same reasoning as in `save.js`. */
function defaultStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
